import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { TreasuryAccountDA } from '../../services/admin/treasury.account.service';
import { Message } from '../../models/message.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { legendIconInvalid, legendInvalid } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { isEqualObjects } from '../../scripts/compare.object.script';
import { getError, getMessage } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppFormTreasuryAccount from '../../interfaces/main/treasury/component/FormTreasuryAccount';
import AppLoading from '../Loading';

export interface AppModalTreasuryAccountUpdateProps {
  companyForUser: Company | null,
  treasuryAccount: TreasuryAccount | null,
  setTreasuryAccount: Dispatch<SetStateAction<TreasuryAccount | null>>,
  loadTreasuryAccountsForCompany: Function
};

let errorResponse: Error, messageResponse: Message;

const AppModalTreasuryAccountUpdate: React.FunctionComponent<AppModalTreasuryAccountUpdateProps> = ({companyForUser, treasuryAccount, setTreasuryAccount, loadTreasuryAccountsForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [nameAccount, setNameAccount] = useState({value: '', valid: false})
  const [iban, setIban] = useState({value: '', valid: false})
  const [bban, setBban] = useState({value: '', valid: false})
  const [bic, setBic] = useState({value: '', valid: false})
  const [membership, setMembership] = useState({value: '', valid: false})
  const [qrIban, setQrIban] = useState({value: '', valid: false})
  const [reference, setReference] = useState({value: '', valid: false})
  const [nameHolder, setNameHolder] = useState({value: '', valid: false})
  const [street, setStreet] = useState({value: '', valid: false})
  const [streetNumber, setStreetNumber] = useState({value: '', valid: false})
  const [additional, setAdditional] = useState({value: '', valid: false})
  const [administrativeArea, setAdministrativeArea] = useState({value: '', valid: false})
  const [city, setCity] = useState({value: '', valid: false})
  const [zipCode, setZipCode] = useState({value: '', valid: false})
  const [country, setCountry] = useState({value: '', valid: false})
  const [options, setOptions] = useState({is_default: false, is_reference: false, is_holder: false})

  const submitTreasuryAccounttUpdate = async () => {
    setLoadIndicator('on')

    if (companyForUser && treasuryAccount && nameAccount.valid && iban.valid && bban.valid && bic.valid && membership.valid && qrIban.valid && reference.valid && reference.valid && nameHolder.valid && street.valid && streetNumber.valid && additional.valid && administrativeArea.valid && city.valid && zipCode.valid && country.valid) {
      let valueIban = iban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueBban = bban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueBic = bic.value.toUpperCase().trim().replaceAll(' ', '')
      let valueQrIban = qrIban.value.toUpperCase().trim().replaceAll(' ', '')
      let addressStreetNumber = (streetNumber.value === 'NN' || streetNumber.value === 'nn') ? streetNumber.value.toUpperCase() : streetNumber.value
      let accountHolder = {full_name: nameHolder.value, address: {street: street.value, number: addressStreetNumber, additional: additional.value, administrative_area: administrativeArea.value, city: city.value, zip_code: zipCode.value, country: country.value}}

      await TreasuryAccountDA.putTreasuryAccount(companyForUser.id, treasuryAccount.id, nameAccount.value, valueIban, valueBban, valueBic, membership.value, valueQrIban, reference.value, options.is_reference, accountHolder).then( (response) => {
        if (response.status === 200) {
          messageResponse = response.data
          loadTreasuryAccountsForCompany(companyForUser.id)

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalTreasuryAccountUpdate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!nameAccount.value) legendInvalid('legend-name-account-required')
        if (!nameHolder.value) legendIconInvalid('input-name-holder', 'legend-name-holder-required')
        if (!street.value) legendIconInvalid('input-street', 'legend-street-required')
        if (!streetNumber.value) legendIconInvalid('input-street-number', 'legend-street-number-required')
        if (!administrativeArea.value) legendIconInvalid('input-administrative-area', 'legend-administrative-area-required')
        if (!city.value) legendIconInvalid('input-city', 'legend-city-required')
        if (!zipCode.value) legendIconInvalid('input-zip-code', 'legend-zip-code-required')
        if (!country.value) legendInvalid('legend-country-required')
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalTreasuryAccountUpdate = () => {
    modalHide('modal-treasury-account-update')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    setTreasuryAccount(null)
  }

  useEffect( () => {
    if (treasuryAccount) {
      setNameAccount({value: treasuryAccount.name, valid: true})
      setIban({value: treasuryAccount.iban, valid: true})
      setBban({value: treasuryAccount.bban, valid: true})
      setBic({value: treasuryAccount.bic, valid: true})
      setMembership({value: treasuryAccount.membership, valid: true})
      setQrIban({value: treasuryAccount.qr_iban, valid: true})
      setReference({value: treasuryAccount.reference, valid: true})
      setNameHolder({value: treasuryAccount.account_holder.full_name, valid: true})
      setStreet({value: treasuryAccount.account_holder.address.street, valid: true})
      setStreetNumber({value: treasuryAccount.account_holder.address.number, valid: true})
      setAdditional({value: treasuryAccount.account_holder.address.additional, valid: true})
      setAdministrativeArea({value: treasuryAccount.account_holder.address.administrative_area, valid: true})
      setCity({value: treasuryAccount.account_holder.address.city, valid: true})
      setZipCode({value: treasuryAccount.account_holder.address.zip_code, valid: true})
      setCountry({value: treasuryAccount.account_holder.address.country, valid: true})
      setOptions({is_default: treasuryAccount.default, is_reference: treasuryAccount.is_reference, is_holder: (!!companyForUser && companyForUser.name === treasuryAccount.account_holder.full_name && isEqualObjects(companyForUser.address, treasuryAccount.account_holder.address))})
    } else {
      setNameAccount({value: '', valid: false})
      setIban({value: '', valid: false})
      setBban({value: '', valid: false})
      setBic({value: '', valid: false})
      setMembership({value: '', valid: false})
      setQrIban({value: '', valid: false})
      setReference({value: '', valid: false})
      setNameHolder({value: '', valid: false})
      setStreet({value: '', valid: false})
      setStreetNumber({value: '', valid: false})
      setAdditional({value: '', valid: false})
      setAdministrativeArea({value: '', valid: false})
      setCity({value: '', valid: false})
      setZipCode({value: '', valid: false})
      setCountry({value: '', valid: false})
      setOptions({is_default: false, is_reference: false, is_holder: false})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treasuryAccount])

  return (
    <div id="modal-treasury-account-update" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.updateTreasuryAccountData}</h2>
          </div>
          <div className="modal-body">
            { companyForUser && treasuryAccount
              ?
              <AppFormTreasuryAccount component={"modal-treasury-account-update"} treasuryAccount={treasuryAccount} nameAccount={nameAccount} iban={iban} bban={bban} bic={bic} membership={membership} qrIban={qrIban} reference={reference} nameHolder={nameHolder} street={street} streetNumber={streetNumber} additional={additional} administrativeArea={administrativeArea} city={city} zipCode={zipCode} country={country} options={options} setNameAccount={setNameAccount} setIban={setIban} setBban={setBban} setBic={setBic} setMembership={setMembership} setQrIban={setQrIban} setReference={setReference} setNameHolder={setNameHolder} setStreet={setStreet} setStreetNumber={setStreetNumber} setAdditional={setAdditional} setAdministrativeArea={setAdministrativeArea} setCity={setCity} setZipCode={setZipCode} setCountry={setCountry} setOptions={setOptions}></AppFormTreasuryAccount>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalTreasuryAccountUpdate}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitTreasuryAccounttUpdate}>
              <span className="indicator-label">{lang.labels.update}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalTreasuryAccountUpdate;
